import React, { useMemo, useCallback } from 'react'
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Box, Stack } from '@mui/material'
import moment from 'moment'

import EmptyScreen from 'packages/ui/EmptyScreen'
import { useTranslation } from 'react-i18next'
import { TimeRange } from 'models'
import ChartSkeleton from 'components/global/CUK/Skeletons/ChartSkeleton'

import TooltipGraph from '../components/TooltipGraph'
import useUtils from '../use-utils'
import useGraph from './use-graph'
import Legend from '../components/Legend'
import useSelection, { useHoveredItem } from '../store'

interface SiteActiveUsersProps {
  modalTimeRange?: TimeRange
  hasLegend: boolean
}

const GraphActiveUsers: React.FC<SiteActiveUsersProps> = ({ modalTimeRange, hasLegend = false }) => {
  const { t } = useTranslation()
  const { customTickFormatter } = useUtils()
  const [selectedItems] = useSelection()
  const [hoveredItem] = useHoveredItem()
  const { loading: trafficLoading, graph: trafficGraph } = useGraph({ modalTimeRange: modalTimeRange as TimeRange })

  const chartData = useMemo(() => {
    if (!selectedItems.length) {
      return []
    }
    
    if (!trafficGraph?.length) {
      return []
    }
    return trafficGraph.map(({ date, sites, is_estimated }, index) => {
      const baseData = {
        timestamp: moment(date).unix() * 1000,
        is_estimated,
      }

      const values = {}
      selectedItems.forEach((checkedSite) => {
        const siteData = sites.find(site => 
          String(site.name || '-') === String(checkedSite.name || '-') && 
          site.site_id === checkedSite.site_id
        )
        const key = `${checkedSite.name || '-'}_${checkedSite.site_id}`
        values[key] = siteData ? siteData.active_users : 0
      })

      const lastPointWasActual = index > 0 && !trafficGraph[index - 1].is_estimated
      const showOnEstimatedLine = is_estimated || lastPointWasActual

      const dataKeys = {}
      Object.entries(values).forEach(([key, value]) => {
        dataKeys[key] = !is_estimated ? value : null
        dataKeys[`${key}_estimated`] = showOnEstimatedLine ? value : null
      })

      return {
        ...baseData,
        ...dataKeys
      }
    })
  }, [modalTimeRange, trafficGraph, selectedItems])

  const hasData = useMemo(() => {
    if (!selectedItems.length || !chartData.length) return false
    return chartData.some(entry => {
      return selectedItems.some(site => {
        const key = `${site.name || '-'}_${site.site_id}`
        return entry[key] > 0 || entry[`${key}_estimated`] > 0
      })
    })
  }, [chartData, selectedItems])

  const legendItems = useMemo(() => {
    return selectedItems.map(item => ({
      label: item.name,
      color: item.color
    })) || []
  }, [selectedItems])

  const getLineOpacity = useCallback((siteIndex: number) => {
    if (hoveredItem) {
      const site = selectedItems[siteIndex]
      const isItemHovered = String(hoveredItem.name || '-') === String(site.name || '-') && 
                           hoveredItem.site_id === site.site_id
      return isItemHovered ? 1 : 0.3
    }
    
    return 1
  }, [hoveredItem, selectedItems])

  if (trafficLoading) {
    return <ChartSkeleton />
  }

  if (!selectedItems.length || !hasData) {
    return (
      <Box sx={{ 
        width: '100%',
        height: '100%',
        border: '1px dashed #D7DAEA',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px'
      }}>
        <EmptyScreen 
          icon="square-poll-vertical" 
          variant="compact"
          iconSize="32px" 
          fontSize="14px"
          spacing={1.5}
        >
          {t('stats.traffic.graphs.no_data')}
        </EmptyScreen>
      </Box>
    )
  }

  const itemCount = selectedItems.filter(item => item.network_name !== 'other').length
  const tooltipPosition = modalTimeRange
    ? { y: -80 }
    : itemCount < 7 ? { y: -(itemCount * 40 + 40) } : { y: -320 }

  return (
    <Stack gap={1} height={'100%'}>
      <ResponsiveContainer>
        <LineChart
          width={500}
          height={300}
          data={chartData}
          margin={{
            top: 60,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <XAxis
            dataKey="timestamp"
            tickFormatter={customTickFormatter}
            style={{
              fontSize: '0.75rem',
              fontWeight: 400,
            }}
          />
          <YAxis
            style={{
              fontSize: '0.75rem',
              fontWeight: 400,
            }}
          />
          <Tooltip
            cursor={false}
            wrapperStyle={{ outline: 'none', zIndex: 9999 }}
            content={
              <TooltipGraph
                dataType={'site'}
                isTraffic={true}
              />
            }
            position={tooltipPosition}
          />

          {selectedItems.map((site, siteIndex) => {
            const key = `${site.name || '-'}_${site.site_id}`
            const opacity = getLineOpacity(siteIndex)
            const estimatedOpacity = getLineOpacity(siteIndex)

            return (
              <React.Fragment key={key}>
                <Line
                  dataKey={key}
                  connectNulls={false}
                  stroke={site.color}
                  fill={site.color}
                  strokeWidth={2}
                  dot={false}
                  strokeOpacity={opacity}
                />
                <Line
                  dataKey={`${key}_estimated`}
                  connectNulls={false}
                  stroke={site.color}
                  fill={site.color}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  strokeOpacity={estimatedOpacity}
                />
              </React.Fragment>
            )
          })}
        </LineChart>
      </ResponsiveContainer>
      {
        hasLegend && <Legend items={legendItems} />
      }
    </Stack>
  )
}

export default GraphActiveUsers